import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';

// Design system Ergofy (glass)
import './styles/glass.css';

// Contexts
import { ThemeModeProvider } from './contexts/ThemeModeContext';
import { AuthProvider } from './contexts/AuthContext';

// Components
import ProtectedRoute from './components/common/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import Loading from './components/common/Loading';
import ErrorBoundary from './components/common/ErrorBoundary';

// Pages
import Login from './pages/auth/Login';

// Lazy loading das páginas principais
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const Companies = React.lazy(() => import('./pages/companies/Companies'));
const Campaigns = React.lazy(() => import('./pages/campaigns/Campaigns'));
const CampaignDetails = React.lazy(() => import('./pages/campaigns/CampaignDetails'));
const Reports = React.lazy(() => import('./pages/reports/Reports'));
const ReportDetail = React.lazy(() => import('./pages/reports/ReportDetail'));
const SurveyForm = React.lazy(() => import('./pages/survey/SurveyForm'));
const KioskPage = React.lazy(() => import('./pages/kiosk/KioskPage'));
const NotFound = React.lazy(() => import('./pages/common/NotFound'));
const Consultorias = React.lazy(() => import('./pages/consultorias/Consultorias'));

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Global Styles para glassmorphism
const globalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      '*': {
        boxSizing: 'border-box',
      },
      html: {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      body: {
        margin: 0,
        padding: 0,
        fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.palette.background.default,
        // Gradient background
        backgroundImage: `
          radial-gradient(circle at 20% 80%, ${theme.palette.primary.main}15 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, ${theme.palette.secondary.main}15 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, ${theme.palette.success.main}10 0%, transparent 50%)
        `,
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
      },
      '#root': {
        minHeight: '100vh',
      },
      // Custom scrollbar
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '::-webkit-scrollbar-thumb': {
        background: theme.palette.divider,
        borderRadius: '4px',
        '&:hover': {
          background: theme.palette.text.secondary,
        },
      },
      // Loading animations
      '@keyframes pulse': {
        '0%': {
          opacity: 1,
        },
        '50%': {
          opacity: 0.5,
        },
        '100%': {
          opacity: 1,
        },
      },
      '.pulse': {
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      },
    })}
  />
);

// Componente para Suspense loading
const SuspenseLoading = ({ children }) => (
  <React.Suspense 
    fallback={
      <Loading 
        fullScreen 
        message="Carregando página..." 
        size={48} 
      />
    }
  >
    {children}
  </React.Suspense>
);

// Componente principal da aplicação
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <CssBaseline />
        {globalStyles}
        
        <SnackbarProvider 
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          autoHideDuration={6000}
          dense
        >
          <ErrorBoundary>
            <AuthProvider>
            <Router>
              <AnimatePresence mode="wait">
                <Routes>
                  {/* Rota pública - Login */}
                  <Route 
                    path="/login" 
                    element={
                      <motion.div
                        key="login"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Login />
                      </motion.div>
                    } 
                  />

                  {/* Rotas protegidas com layout */}
                  <Route 
                    path="/" 
                    element={
                      <ProtectedRoute>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Redirect root para dashboard */}
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    
                    {/* Dashboard - Todos os usuários autenticados */}
                    <Route 
                      path="dashboard" 
                      element={
                        <SuspenseLoading>
                          <Dashboard />
                        </SuspenseLoading>
                      } 
                    />
                    
                    {/* Empresas - Manager+ */}
                    <Route 
                      path="companies" 
                      element={
                        <ProtectedRoute requiredRoles={['manager', 'admin', 'owner']}>
                          <SuspenseLoading>
                            <Companies />
                          </SuspenseLoading>
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Campanhas - Viewer+ */}
                    <Route 
                      path="campaigns" 
                      element={
                        <ProtectedRoute requiredRoles={['viewer', 'analyst', 'manager', 'admin', 'owner']}>
                          <SuspenseLoading>
                            <Campaigns />
                          </SuspenseLoading>
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Detalhes da Campanha - Viewer+ */}
                    <Route 
                      path="campaigns/:id" 
                      element={
                        <ProtectedRoute requiredRoles={['viewer', 'analyst', 'manager', 'admin', 'owner']}>
                          <SuspenseLoading>
                            <CampaignDetails />
                          </SuspenseLoading>
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Relatórios - Viewer+ */}
                    <Route 
                      path="reports" 
                      element={
                        <ProtectedRoute requiredRoles={['viewer', 'analyst', 'manager', 'admin', 'owner']}>
                          <SuspenseLoading>
                            <Reports />
                          </SuspenseLoading>
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Relatório Detalhado - Viewer+ */}
                    <Route 
                      path="reports/:surveyRunId" 
                      element={
                        <ProtectedRoute requiredRoles={['viewer', 'analyst', 'manager', 'admin', 'owner']}>
                          <SuspenseLoading>
                            <ReportDetail />
                          </SuspenseLoading>
                        </ProtectedRoute>
                      }
                    />

                    {/* Consultorias - super-admin de plataforma (guarda na própria página) */}
                    <Route
                      path="consultorias"
                      element={
                        <SuspenseLoading>
                          <Consultorias />
                        </SuspenseLoading>
                      }
                    />
                  </Route>

                  {/* Rota para formulário público (sem autenticação) */}
                  <Route 
                    path="/survey/:token" 
                    element={
                      <SuspenseLoading>
                        <SurveyForm />
                      </SuspenseLoading>
                    } 
                  />

                  {/* Quiosque presencial (sem login de usuário: acesso por
                      código do responsável, validado no backend) */}
                  <Route
                    path="/quiosque"
                    element={
                      <SuspenseLoading>
                        <KioskPage />
                      </SuspenseLoading>
                    }
                  />

                  {/* Rota de pré-visualização (autenticado Viewer+) */}
                  <Route 
                    path="/survey/preview/:surveyRunId" 
                    element={
                      <ProtectedRoute requiredRoles={['viewer', 'analyst', 'manager', 'admin', 'owner']}>
                        <SuspenseLoading>
                          <SurveyForm previewMode />
                        </SuspenseLoading>
                      </ProtectedRoute>
                    } 
                  />

                  {/* 404 - Página não encontrada */}
                  <Route 
                    path="*" 
                    element={
                      <SuspenseLoading>
                        <NotFound />
                      </SuspenseLoading>
                    } 
                  />
                </Routes>
              </AnimatePresence>
            </Router>
            </AuthProvider>
          </ErrorBoundary>
        </SnackbarProvider>
      </ThemeModeProvider>
    </QueryClientProvider>
  );
}

export default App;